import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useId, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { buildTeamCallInReasonsUrl } from "../../../../components/team/CallInReasons/deepLink";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { EyebrowLabel } from "../../../../components/UIToolkit/molecules/EyebrowLabel";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { ALPHA, FONT_SIZE, INK, POPOVER_PANEL_WIDTH } from "../../../../theme/tokens";
import type { HiringAbsenceSettings, HiringAbsenceSettingsUpdate } from "../types";
import { countedAbsenceItems } from "../utils/absenceSettings";

type Props = {
	settings: HiringAbsenceSettings;
	departmentId: string;
	saving: boolean;
	/** Only the reasons whose box changed are sent; the rest keep their decision. */
	onSave: (update: HiringAbsenceSettingsUpdate) => void;
	onClose: () => void;
};

type Draft = { countsPto: boolean; countsReplaceRequests: boolean; reasons: Record<string, boolean> };

const draftFrom = (settings: HiringAbsenceSettings): Draft => ({
	countsPto: settings.countsPto,
	countsReplaceRequests: settings.countsReplaceRequests,
	reasons: Object.fromEntries(settings.reasons.map((reason) => [reason.id, reason.countsAsAbsence])),
});

/** A checked row sits on a brand wash, so the counted items read as one group at a glance. */
const ChecklistRow: FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void; dataTestId: string }> = ({
	label,
	checked,
	onChange,
	dataTestId,
}) => (
	<SystemRoundedCheckbox
		label={label}
		checked={checked}
		onChange={onChange}
		fullWidth
		data-testid={dataTestId}
		sx={{
			px: 1.5,
			py: 1,
			borderRadius: 1,
			bgcolor: (theme) => (checked ? alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) : "transparent"),
			transition: (theme) => theme.transitions.create("background-color", { duration: theme.transitions.duration.shortest }),
		}}
	/>
);

/** The Hiring page's "What counts as an absence?" checklist (DAT-850). Nothing is written until Save. */
const AbsenceSettingsPanel: FC<Props> = ({ settings, departmentId, saving, onSave, onClose }) => {
	const { t } = useTranslation();
	const titleId = useId();
	const [draft, setDraft] = useState<Draft>(() => draftFrom(settings));
	const setReason = (id: string, counts: boolean) => setDraft((current) => ({ ...current, reasons: { ...current.reasons, [id]: counts } }));
	const setAllReasons = (counts: boolean) =>
		setDraft((current) => ({ ...current, reasons: Object.fromEntries(settings.reasons.map((reason) => [reason.id, counts])) }));
	const { counted, total } = countedAbsenceItems({
		countsPto: draft.countsPto,
		countsReplaceRequests: draft.countsReplaceRequests,
		reasonDecisions: settings.reasons.map((reason) => draft.reasons[reason.id]),
	});

	const handleSave = () =>
		onSave({
			countsPto: draft.countsPto,
			countsReplaceRequests: draft.countsReplaceRequests,
			reasons: settings.reasons
				.filter((reason) => draft.reasons[reason.id] !== reason.countsAsAbsence)
				.map((reason) => ({ reasonId: reason.id, countsAsAbsence: draft.reasons[reason.id] })),
		});

	return (
		<Box
			role="dialog"
			aria-labelledby={titleId}
			onClick={(event) => event.stopPropagation()}
			data-testid="hiring-absence-settings-dialog"
			sx={{ width: POPOVER_PANEL_WIDTH.CHECKLIST, display: "flex", flexDirection: "column" }}
		>
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 2.5, pt: 2.5, pb: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Typography id={titleId} component="h2" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, color: INK.PRIMARY }}>
						{t("hiring.absenceSettings.title")}
					</Typography>
					<Typography sx={{ mt: 0.5, fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("hiring.absenceSettings.subtitle")}</Typography>
				</Box>
				<IconButton size="small" onClick={onClose} aria-label={t("close")} data-testid="hiring-absence-settings-close">
					<CloseIcon fontSize="small" />
				</IconButton>
			</Box>

			<Box sx={{ px: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
				<EyebrowLabel component="h3">{t("hiring.absenceSettings.timeOff")}</EyebrowLabel>
				<ChecklistRow
					label={t("hiring.absenceSettings.pto")}
					checked={draft.countsPto}
					onChange={(countsPto) => setDraft((current) => ({ ...current, countsPto }))}
					dataTestId="hiring-absence-settings-pto"
				/>
				<ChecklistRow
					label={t("hiring.absenceSettings.replaceRequests")}
					checked={draft.countsReplaceRequests}
					onChange={(countsReplaceRequests) => setDraft((current) => ({ ...current, countsReplaceRequests }))}
					dataTestId="hiring-absence-settings-replace-requests"
				/>
			</Box>

			<Box sx={{ mx: 2.5, my: 2, borderTop: 1, borderColor: "divider" }} />

			<Box sx={{ px: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<EyebrowLabel component="h3" sx={{ flex: 1 }}>
						{t("hiring.absenceSettings.callOffReasons")}
					</EyebrowLabel>
					{settings.reasons.length > 0 && (
						<>
							<SystemButton buttonVariant="borderless" size="compact" onClick={() => setAllReasons(true)} data-testid="hiring-absence-settings-select-all">
								{t("hiring.absenceSettings.selectAll")}
							</SystemButton>
							<SystemButton buttonVariant="borderless" size="compact" onClick={() => setAllReasons(false)} data-testid="hiring-absence-settings-clear">
								{t("hiring.absenceSettings.clear")}
							</SystemButton>
						</>
					)}
				</Box>
				{settings.reasons.length === 0 ? (
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid="hiring-absence-settings-no-reasons">
						{t("hiring.absenceSettings.noReasons")}
					</Typography>
				) : (
					settings.reasons.map((reason) => (
						<ChecklistRow
							key={reason.id}
							label={reason.label}
							checked={draft.reasons[reason.id]}
							onChange={(counts) => setReason(reason.id, counts)}
							dataTestId={`hiring-absence-settings-reason-${reason.id}`}
						/>
					))
				)}
				<InlineHint tone="neutral" contentComponent="div" sx={{ mt: 1 }} dataTestId="hiring-absence-settings-shared-note">
					<Trans
						i18nKey="hiring.absenceSettings.sharedNote"
						components={{ teamLink: <Link component={RouterLink} to={buildTeamCallInReasonsUrl(departmentId)} underline="always" /> }}
					/>
				</InlineHint>
			</Box>

			<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 2, mt: 2, borderTop: 1, borderColor: "divider" }}>
				<Typography sx={{ flex: 1, fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid="hiring-absence-settings-count">
					{t("hiring.absenceSettings.countedOf", { counted, total })}
				</Typography>
				<SystemButton buttonVariant="hairline" onClick={onClose} disabled={saving} noFullWidthMobile data-testid="hiring-absence-settings-cancel">
					{t("cancel")}
				</SystemButton>
				<SystemButton onClick={handleSave} loading={saving} noFullWidthMobile data-testid="hiring-absence-settings-confirm">
					{t("save")}
				</SystemButton>
			</Box>
		</Box>
	);
};

export default AbsenceSettingsPanel;
